/**
 * SPOT radio EEPROM/storage layout helpers.
 *
 * Two address spaces matter here:
 * - the legacy 16-bit EEPROM compatibility view exposed over the UART protocol
 * - the physical flash layout used internally by firmware
 *
 * SPOT Editor talks only to the 16-bit compatibility view. New SPOT-owned
 * blocks therefore need stable virtual EEPROM offsets even when their physical
 * backing storage lives above 0xFFFF in external flash.
 */

export const CHANNEL_COUNT = 200;
export const LEGACY_PHYSICAL_CHANNEL_COUNT = 1024;
export const VISIBLE_SCAN_LIST_COUNT = 16;
export const STORAGE_SCAN_LIST_COUNT = 32;

export const CHANNEL_RECORD_SIZE = 16;
export const CHANNEL_NAME_SIZE = 16;
export const CHANNEL_NAME_TEXT_SIZE = 10;
export const ATTR_WORD_SIZE = 2;
export const WRITE_BLOCK_SIZE = 8;

export const SPOT_METADATA_OFFSET = 0xa170;
export const SPOT_METADATA_SIZE = 8;
export const SPOT_METADATA_COMPATIBILITY_VERSION = 3;
export const SPOT_METADATA_VERSION = 2;
export const SPOT_METADATA_MAGIC = 0xa5;
export const SPOT_CAP_FREQUENCY_OFFSET = 0x01;
export const SPOT_CAP_CHANNEL_JOURNAL = 0x02;

export const RECORDS_BASE_OFFSET = 0x0000;
export const NAMES_BASE_OFFSET = 0x4000;
export const ATTR_BASE_OFFSET = 0x8000;

export const RECORDS_TOTAL_BYTES = CHANNEL_COUNT * CHANNEL_RECORD_SIZE;
export const NAMES_TOTAL_BYTES = CHANNEL_COUNT * CHANNEL_NAME_SIZE;
export const ATTR_TOTAL_BYTES = CHANNEL_COUNT * ATTR_WORD_SIZE;

export const SPOT_LIST_NAME_SIZE = 16;
export const SPOT_LIST_NAME_UI_LEN = 10;
export const SPOT_LIST_NAMES_TOTAL_BYTES = STORAGE_SCAN_LIST_COUNT * SPOT_LIST_NAME_SIZE;
export const NAMES_STORAGE_TOTAL_BYTES = LEGACY_PHYSICAL_CHANNEL_COUNT * CHANNEL_NAME_SIZE;
export const NAMES_STORAGE_END_OFFSET = NAMES_BASE_OFFSET + NAMES_STORAGE_TOTAL_BYTES;
// Allocations grow backward from the end of the legacy name storage.
export const SPOT_LIST_NAMES_OFFSET = NAMES_STORAGE_END_OFFSET - SPOT_LIST_NAMES_TOTAL_BYTES;

export function getDefaultVisibleScanListName(index: number): string {
  return index === (VISIBLE_SCAN_LIST_COUNT - 1) ? "Ad Hoc" : `L${index + 1}`;
}

export const SPOT_LIST_GLOBALS_OFFSET = 0xd200;
export const SPOT_LIST_GLOBALS_SIZE = 16;
export const SPOT_LIST_GLOBALS_VERSION = 1;

export const SPOT_RECEIVER_SETTINGS_OFFSET = 0xd210;
export const SPOT_RECEIVER_SETTINGS_SIZE = 16;
export const SPOT_RECEIVER_SETTINGS_VERSION = 1;
export const SPOT_RECEIVER_SETTINGS_MAGIC = 0xa5;

export const SPOT_CHANNEL_EXT_OFFSET = 0xd400;
export const SPOT_CHANNEL_EXT_SIZE = 8;
export const SPOT_CHANNEL_EXT_TOTAL_BYTES = CHANNEL_COUNT * SPOT_CHANNEL_EXT_SIZE;

export function getChannelRecordOffset(index: number): number {
  return RECORDS_BASE_OFFSET + (index * CHANNEL_RECORD_SIZE);
}

export function getChannelNameSlotOffset(index: number): number {
  return NAMES_BASE_OFFSET + (index * CHANNEL_NAME_SIZE);
}

export function getChannelAttrOffset(index: number): number {
  return ATTR_BASE_OFFSET + (index * ATTR_WORD_SIZE);
}

export function getChannelNameTextRange(index: number): [number, number] {
  const start = getChannelNameSlotOffset(index);
  return [start, start + CHANNEL_NAME_TEXT_SIZE];
}

export function getSpotListNameOffset(index: number): number {
  return SPOT_LIST_NAMES_OFFSET + (index * SPOT_LIST_NAME_SIZE);
}

export function getSpotChannelExtOffset(index: number): number {
  return SPOT_CHANNEL_EXT_OFFSET + (index * SPOT_CHANNEL_EXT_SIZE);
}
